import {useMutation} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CopilotChatReference} from '../utils/copilot-chat-types'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import type {ReferenceMention, ReferenceType} from '../utils/reference-mention'

const getTypeParam = (itemRefType: Exclude<ReferenceType, 'repository'>): string => {
  switch (itemRefType) {
    case 'issue':
      return 'issue'
    case 'pull-request':
      return 'pull_request'
    case 'discussion':
      return 'discussion'
    case 'thread-scoped-file':
    case 'file':
      return 'file'
  }
}

export function useAddMentionReferences() {
  const manager = useChatManager()

  // This is probably a misuse of `useMutation`, but it's a decent compromise that allows us to fetch imperatively.
  // It does unfortunately mean that we don't get any caching, since mutations aren't cached.

  const mentionReferencesQuery = useMutation({
    mutationFn: async (mention: ReferenceMention) => {
      const pathParams =
        mention.type === 'repository' ? [mention.repo] : [mention.repo, getTypeParam(mention.type), mention.id ?? '']
      const response = await verifiedFetchJSON(`/copilot/chat/reference/${pathParams.join('/')}`)
      if (!response.ok) throw new Error(`Failed to fetch reference data (${response.status}: ${response.statusText})`)
      return (await response.json()) as CopilotChatReference
    },
    onSuccess: reference => manager.addReference(reference, 'chatInput'),
  })

  return {
    isLoading: mentionReferencesQuery.isPending,
    addReferenceForMention: mentionReferencesQuery.mutate,
  }
}
